import { ErrorCode, type FetchOrgSettingsSuccessResponse } from "@propelauth/frontend-apis";
import { MfaStatus, useAuthFrontendApis, useMfa } from "@propelauth/frontend-apis-react";
import { useHostedPageUrls, useLogoutFunction, useOrgHelper } from "@propelauth/react";
import {
  Building2,
  Check,
  Copy,
  Download,
  ExternalLink,
  Globe,
  Info,
  Link2,
  Trash2,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Switch } from "../../../../components/ui/switch";
import useHeaderStore from "../../../../store/layout/headerStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";

const MIN_PASSWORD_LEN = 8;

const toQrImageSrc = (qrValue?: string) => {
  if (!qrValue) return null;
  const value = qrValue.trim();
  if (!value) return null;
  if (value.startsWith("data:image/")) return value;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `data:image/png;base64,${value}`;
};

type SecuritySettingsPageProps = {
  embedded?: boolean;
};

type TwoFactorPolicy = "optional" | "admins" | "all";

type ApiError = {
  error_code?: string;
  user_facing_errors?: {
    password?: string;
  };
  field_errors?: {
    code?: string;
  };
};

const SecuritySettingsPage: React.FC<SecuritySettingsPageProps> = ({ embedded = false }) => {
  const { setHeaderContent, resetHeader } = useHeaderStore();
  const logout = useLogoutFunction();
  const authApis = useAuthFrontendApis();
  const { orgHelper } = useOrgHelper();
  const { getSetupSAMLPageUrl } = useHostedPageUrls();
  const { status: mfaStatus, data: mfaData, enableMfa, disableMfa, reloadMfaStatus } = useMfa();
  const workspace = useWorkspaceStore((state) => state.workspace);

  const [loading, setLoading] = useState(false);
  const [orgSettingsLoading, setOrgSettingsLoading] = useState(false);
  const [orgSettings, setOrgSettings] = useState<FetchOrgSettingsSuccessResponse | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaDisableConfirmOpen, setMfaDisableConfirmOpen] = useState(false);
  const [backupCodesOpen, setBackupCodesOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const [twoFaPolicy, setTwoFaPolicy] = useState<TwoFactorPolicy>("optional");
  const [autojoinByDomain, setAutojoinByDomain] = useState(false);
  const [restrictToDomain, setRestrictToDomain] = useState(false);

  const isMfaEnabled = mfaStatus === MfaStatus.Enabled;
  const isMfaBusy = mfaStatus === MfaStatus.Loading || mfaStatus === MfaStatus.UnexpectedError;

  const orgId = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return null;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.orgId ?? null;
  }, [orgHelper, workspace?.unique_identifier]);

  const policyFromSettings = useMemo<TwoFactorPolicy>(() => (
    orgSettings?.require_2fa_by ? "all" : "optional"
  ), [orgSettings?.require_2fa_by]);

  const domainRows = useMemo(() => {
    const orgDomain = String(orgSettings?.existing_domain || "").trim();
    const workspaceDomain = String(workspace?.tracking_website_domain || "").trim();
    const domain = orgDomain || workspaceDomain;
    if (!domain) return [] as string[];
    return [domain.replace(/^https?:\/\//, "").replace(/\/$/, "")];
  }, [orgSettings?.existing_domain, workspace?.tracking_website_domain]);

  const mfaSetupData = useMemo(() => {
    if (mfaStatus !== MfaStatus.Disabled || !mfaData || typeof mfaData !== "object") return null;
    if (!("new_qr" in mfaData)) return null;
    return mfaData as { new_qr?: string; new_secret?: string };
  }, [mfaData, mfaStatus]);

  const mfaQrSrc = useMemo(
    () => toQrImageSrc(mfaSetupData?.new_qr),
    [mfaSetupData?.new_qr],
  );

  const ssoBadgeStatus = useMemo<"active" | "open">(() => (
    orgSettings?.is_saml_enabled ? "active" : "open"
  ), [orgSettings?.is_saml_enabled]);

  useEffect(() => {
    if (embedded) return undefined;
    resetHeader();
    setHeaderContent({
      title: "Security",
      description: "Manage your password and security settings",
    });

    return () => resetHeader();
  }, [embedded, resetHeader, setHeaderContent]);

  useEffect(() => {
    if (!orgId) {
      setOrgSettings(null);
      return;
    }

    setOrgSettingsLoading(true);
    authApis.fetchOrgSettings(orgId).then((response) => {
      if (!response.ok) {
        setOrgSettings(null);
        return;
      }
      setOrgSettings(response.data);
      setTwoFaPolicy(response.data.require_2fa_by ? "all" : "optional");
      setAutojoinByDomain(response.data.autojoin_by_domain ?? false);
      setRestrictToDomain(response.data.restrict_to_domain ?? false);
    }).catch(() => {
      setOrgSettings(null);
    }).finally(() => {
      setOrgSettingsLoading(false);
    });
  }, [authApis, orgId]);

  const resetErrors = () => {
    setPasswordError(null);
    setMfaError(null);
  };

  const handleUpdatePassword = async () => {
    resetErrors();

    const trimmedCurrent = currentPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedCurrent || !trimmedNew || !trimmedConfirm) {
      setPasswordError("All password fields are required");
      return;
    }

    if (trimmedNew.length < MIN_PASSWORD_LEN) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LEN} characters`);
      return;
    }

    if (trimmedNew !== trimmedConfirm) {
      setPasswordError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await authApis.updatePassword({
        password: trimmedNew,
        current_password: trimmedCurrent,
      });

      if (response.ok) {
        notify({ type: "success", messageList: ["Password updated successfully."] });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordModalOpen(false);
        return;
      }

      const error = response.error as ApiError;
      if (error?.user_facing_errors?.password) {
        setPasswordError(error.user_facing_errors.password);
        return;
      }
      if (error?.error_code === ErrorCode.IncorrectPassword) {
        setPasswordError("Current password is incorrect");
        return;
      }

      notify({ type: "failure", heading: "Unable to update password", messageList: ["Please try again later."] });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMfa = (nextChecked: boolean) => {
    if (isMfaBusy || loading) return;

    if (nextChecked && mfaStatus === MfaStatus.Disabled) {
      setMfaCode("");
      setMfaError(null);
      setMfaSetupOpen(true);
      reloadMfaStatus().catch(() => {
        notify({
          type: "failure",
          heading: "Unable to refresh 2FA setup",
          messageList: ["Please close and reopen the dialog."],
        });
      });
      return;
    }

    if (!nextChecked && mfaStatus === MfaStatus.Enabled) {
      setMfaDisableConfirmOpen(true);
    }
  };

  const handleEnableMfa = async () => {
    resetErrors();
    if (mfaStatus !== MfaStatus.Disabled) return;

    if (!mfaCode.trim()) {
      setMfaError("Code is required");
      return;
    }

    setLoading(true);
    try {
      const response = await enableMfa({ code: mfaCode.trim() });
      if (response.ok) {
        notify({ type: "success", messageList: ["Two-factor authentication enabled."] });
        setMfaSetupOpen(false);
        setMfaCode("");

        const enabledData = response.data as { backup_codes?: string[] } | undefined;
        if (enabledData?.backup_codes?.length) {
          setBackupCodes(enabledData.backup_codes);
          setBackupCodesOpen(true);
        }

        await reloadMfaStatus();
        return;
      }

      const error = response.error as ApiError;
      if (error?.field_errors?.code) {
        setMfaError("Invalid code");
        return;
      }

      notify({ type: "failure", heading: "Unable to enable 2FA", messageList: ["Please try again later."] });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBackupCodes = useCallback(() => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n")).then(() => {
      setCopiedBackupCodes(true);
      setTimeout(() => setCopiedBackupCodes(false), 2000);
    }).catch(() => {
      notify({ type: "failure", messageList: ["Failed to copy to clipboard."] });
    });
  }, [backupCodes]);

  const handleDownloadBackupCodes = useCallback(() => {
    if (!backupCodes) return;
    const blob = new Blob([backupCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }, [backupCodes]);

  const handleSetupSaml = useCallback(() => {
    if (!orgId) {
      notify({ type: "failure", messageList: ["Organization not found."] });
      return;
    }
    if (!orgSettings?.can_setup_saml) {
      notify({ type: "info", messageList: ["SSO setup is not available for this organization. Please contact support to enable enterprise SSO."] });
      return;
    }
    const url = getSetupSAMLPageUrl(orgId, { redirectBackToUrl: window.location.href });
    window.open(url, "_blank", "noopener,noreferrer");
  }, [orgId, orgSettings?.can_setup_saml, getSetupSAMLPageUrl]);

  const handleToggleAutojoinByDomain = async (checked: boolean) => {
    if (!orgId) return;
    setAutojoinByDomain(checked);
    setLoading(true);
    try {
      const response = await authApis.updateOrgSettings({
        org_id: orgId,
        allow_users_to_join_by_domain: checked,
      });
      if (!response.ok) {
        setAutojoinByDomain(!checked);
        notify({ type: "failure", heading: "Unable to update domain settings", messageList: ["Please try again later."] });
        return;
      }
      setOrgSettings((prev) => prev ? { ...prev, autojoin_by_domain: checked } : prev);
      notify({ type: "success", messageList: [checked ? "Auto-join by domain enabled." : "Auto-join by domain disabled."] });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRestrictToDomain = async (checked: boolean) => {
    if (!orgId) return;
    setRestrictToDomain(checked);
    setLoading(true);
    try {
      const response = await authApis.updateOrgSettings({
        org_id: orgId,
        restrict_invites_by_domain: checked,
      });
      if (!response.ok) {
        setRestrictToDomain(!checked);
        notify({ type: "failure", heading: "Unable to update domain settings", messageList: ["Please try again later."] });
        return;
      }
      setOrgSettings((prev) => prev ? { ...prev, restrict_to_domain: checked } : prev);
      notify({ type: "success", messageList: [checked ? "Invitations restricted to domain." : "Domain restriction removed."] });
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    resetErrors();
    if (mfaStatus !== MfaStatus.Enabled) return;

    setLoading(true);
    try {
      const response = await disableMfa();
      if (response.ok) {
        notify({ type: "success", messageList: ["Two-factor authentication disabled."] });
        setMfaDisableConfirmOpen(false);
        await reloadMfaStatus();
        return;
      }

      notify({ type: "failure", heading: "Unable to disable 2FA", messageList: ["Please try again later."] });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const response = await authApis.deleteAccount();
      if (response.ok) {
        logout(true);
        return;
      }

      notify({
        type: "failure",
        heading: "Unable to delete account",
        messageList: ["Please contact support or try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFaPolicyChange = async (nextPolicy: TwoFactorPolicy) => {
    setTwoFaPolicy(nextPolicy);

    if (!orgId) return;
    if (nextPolicy === "admins") {
      notify({
        type: "info",
        messageList: ["Admins-only 2FA policy is not available in PropelAuth frontend APIs yet."],
      });
      return;
    }

    setLoading(true);
    try {
      const response = await authApis.updateOrgSettings({
        org_id: orgId,
        require_2fa_by: nextPolicy === "all" ? new Date() : null,
      });

      if (!response.ok) {
        setTwoFaPolicy(policyFromSettings);
        notify({
          type: "failure",
          heading: "Unable to update 2FA policy",
          messageList: ["Please try again later."],
        });
        return;
      }

      setOrgSettings((previous) => (previous ? {
        ...previous,
        require_2fa_by: nextPolicy === "all" ? new Date().toISOString() : null,
      } : previous));
      notify({ type: "success", messageList: ["Two-factor policy updated."] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative ${embedded ? "" : "mx-auto max-w-5xl px-6 py-6"}`}>
      {loading || orgSettingsLoading ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      ) : null}

      <div className={`space-y-8 ${embedded ? "" : "mt-6"}`}>
        <div>
          <h1 className="text-[15px] font-semibold text-foreground">Security</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Manage workspace security and access controls</p>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <h2 className="text-[13px] font-medium text-foreground">SSO</h2>
            <span className="flex items-center gap-1.5 rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
              <Building2 className="h-3 w-3" />
              Organization
            </span>
          </div>
          <p className="mb-3 text-[11px] text-muted-foreground">Configure a SSO connection</p>

          <div className="rounded-lg border border-border">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-3">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-[13px] text-foreground">Workspace SSO</span>
                <Badge variant="statusPill" status={ssoBadgeStatus}>
                  {orgSettings?.is_saml_enabled ? "Active" : "Inactive"}
                </Badge>
                {orgSettings?.is_saml_in_test_mode ? (
                  <Badge variant="statusPill" status="open">Test Mode</Badge>
                ) : null}
              </div>
              {orgSettings?.is_saml_enabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-[12px]"
                  onClick={handleSetupSaml}
                >
                  Manage
                  <ExternalLink className="h-3 w-3" />
                </Button>
              ) : null}
            </div>
            {!orgSettings?.is_saml_enabled ? (
              <button
                type="button"
                className="w-full px-4 py-2.5 text-left text-[13px] text-primary hover:bg-muted disabled:text-muted-foreground disabled:hover:bg-transparent"
                disabled={!orgId || !orgSettings?.can_setup_saml}
                onClick={handleSetupSaml}
              >
                {orgSettings?.can_setup_saml ? "Configure SSO" : "SSO not available for this plan"}
              </button>
            ) : null}
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Email Domain Policy</h2>
          <p className="mb-3 text-[11px] text-muted-foreground">
            Control how users with matching email domains can access this workspace
          </p>
          <div className="rounded-lg border border-border">
            {domainRows.length ? domainRows.map((domain) => (
              <div key={domain} className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-[13px] font-medium text-foreground">{domain}</span>
                  {orgSettings?.current_user_domain_is_personal ? (
                    <span className="text-[11px] text-muted-foreground">(personal domain)</span>
                  ) : null}
                </div>
              </div>
            )) : (
              <div className="px-4 py-3 text-[12px] text-muted-foreground">No domain configured for this organization</div>
            )}

            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div>
                <p className="text-[13px] text-foreground">Auto-join by domain</p>
                <p className="text-[11px] text-muted-foreground">
                  Anyone with a matching email domain can automatically join this workspace
                </p>
              </div>
              <Switch
                checked={autojoinByDomain}
                onCheckedChange={(checked) => {
                  handleToggleAutojoinByDomain(checked).catch(() => {});
                }}
                disabled={loading || !orgId || !domainRows.length}
                aria-label="Toggle auto-join by domain"
              />
            </div>

            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-[13px] text-foreground">Restrict invitations to domain</p>
                <p className="text-[11px] text-muted-foreground">
                  Only users with a matching email domain can be invited
                </p>
              </div>
              <Switch
                checked={restrictToDomain}
                onCheckedChange={(checked) => {
                  handleToggleRestrictToDomain(checked).catch(() => {});
                }}
                disabled={loading || !orgId || !domainRows.length}
                aria-label="Toggle restrict invitations to domain"
              />
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Authentication</h2>
          <p className="mb-3 text-[11px] text-muted-foreground">Customize your workspace security</p>

          <div className="divide-y divide-border rounded-lg border border-border">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-[13px] text-foreground">Password</p>
                <p className="text-[11px] text-muted-foreground">Update your account password</p>
              </div>
              <Button variant="outline" size="sm" className="h-7 text-[12px]" onClick={() => setPasswordModalOpen(true)}>
                Change
              </Button>
            </div>

            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-[13px] text-foreground">Two-Factor Authentication</p>
                <p className="text-[11px] text-muted-foreground">Add an extra layer of security to your account</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="statusPill" status={isMfaEnabled ? "active" : "open"}>
                  {isMfaEnabled ? "Enabled" : "Disabled"}
                </Badge>
                <Switch
                  checked={isMfaEnabled}
                  onCheckedChange={handleToggleMfa}
                  disabled={isMfaBusy || loading}
                  aria-label="Toggle two-factor authentication"
                />
              </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-[13px] text-foreground">Magic Links</p>
                <p className="text-[11px] text-muted-foreground">Passwordless login by email links</p>
              </div>
              <Switch checked={false} disabled aria-label="Magic links coming soon" />
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Two-Factor Authentication Policy</h2>
          <p className="mb-3 text-[11px] text-muted-foreground">Require members to enable 2FA</p>

          <div className="space-y-2">
            {([
              { value: "optional", label: "Optional", desc: "Members can choose to enable 2FA" },
              { value: "admins", label: "Required for admins", desc: "Admins must enable 2FA to access the workspace" },
              { value: "all", label: "Required for all", desc: "All members must enable 2FA to access the workspace" },
            ] as const).map((option) => (
              <label
                key={option.value}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
                  twoFaPolicy === option.value ? "border-primary bg-accent-tint" : "border-border hover:bg-muted"
                }`}
              >
                <input
                  type="radio"
                  value={option.value}
                  checked={twoFaPolicy === option.value}
                  onChange={() => {
                    handleTwoFaPolicyChange(option.value).catch(() => {});
                  }}
                  className="sr-only"
                />
                <div className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${twoFaPolicy === option.value ? "border-primary" : "border-muted-foreground/40"}`}>
                  {twoFaPolicy === option.value ? <div className="h-2 w-2 rounded-full bg-primary" /> : null}
                </div>
                <div>
                  <p className="text-[13px] text-foreground">{option.label}</p>
                  <p className="text-[11px] text-muted-foreground">{option.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Session Management</h2>
          <p className="mb-3 text-[11px] text-muted-foreground">Manage active sessions for your account</p>

          <div className="rounded-lg border border-border">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-[13px] text-foreground">Sign out all sessions</p>
                <p className="text-[11px] text-muted-foreground">
                  Log out of all devices including this one
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[12px]"
                onClick={() => {
                  logout(true);
                }}
              >
                Sign out all
              </Button>
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2.5">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="text-[11px] text-muted-foreground">
              Session duration and concurrent session limits are configured at the project level in your authentication provider dashboard.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-status-cancelled" />
            <h2 className="text-[13px] font-medium text-foreground">Danger Zone</h2>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Once you delete your account, there is no going back. All data will be permanently removed.
          </p>
          <div className="mt-3">
            <Button size="sm" variant="destructive" className="h-8 text-[12px]" onClick={() => setDeleteAccountOpen(true)}>
              Delete account
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Update your password to keep your account secure.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground" htmlFor="current-password">Current password</label>
              <Input id="current-password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground" htmlFor="new-password">New password</label>
              <Input id="new-password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
              <p className="mt-1 text-[11px] text-muted-foreground">Minimum 8 characters</p>
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground" htmlFor="confirm-password">Confirm new password</label>
              <Input id="confirm-password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </div>

            {passwordError ? <p className="text-[12px] text-status-cancelled">{passwordError}</p> : null}
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => {
              handleUpdatePassword().catch(() => {});
            }}>
              Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mfaSetupOpen} onOpenChange={setMfaSetupOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
            <DialogDescription>Scan the QR code in your authenticator app and enter the generated code.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {mfaQrSrc ? (
              <div className="rounded-lg border border-border bg-muted p-4">
                <p className="text-[12px] text-muted-foreground">Scan this QR code with your authenticator app:</p>
                <img src={mfaQrSrc} alt="MFA QR Code" className="mt-3 h-48 w-48 rounded bg-white p-2" />
                {mfaSetupData?.new_secret ? (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Or enter manually: <span className="font-mono text-foreground">{mfaSetupData?.new_secret}</span>
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground" htmlFor="mfa-code">Authentication code</label>
              <Input
                id="mfa-code"
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
                placeholder="Enter 6-digit code"
              />
              {mfaError ? <p className="mt-1 text-[12px] text-status-cancelled">{mfaError}</p> : null}
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setMfaSetupOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={() => {
              handleEnableMfa().catch(() => {});
            }}>
              Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mfaDisableConfirmOpen} onOpenChange={setMfaDisableConfirmOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Disabling 2FA will reduce the security of your account. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setMfaDisableConfirmOpen(false)}>Cancel</Button>
            <Button size="sm" variant="destructive" onClick={() => {
              handleDisableMfa().catch(() => {});
            }}>
              Disable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={backupCodesOpen} onOpenChange={(open) => {
        if (!open) {
          setBackupCodesOpen(false);
          setBackupCodes(null);
          setCopiedBackupCodes(false);
        }
      }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Backup Codes</DialogTitle>
            <DialogDescription>
              Save these backup codes in a safe place. You can use each code once to sign in if you lose access to your authenticator app.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted p-4">
              {backupCodes?.map((code) => (
                <span key={code} className="font-mono text-[13px] text-foreground">{code}</span>
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-[12px]"
                onClick={handleCopyBackupCodes}
              >
                {copiedBackupCodes ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copiedBackupCodes ? "Copied" : "Copy"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-[12px]"
                onClick={handleDownloadBackupCodes}
              >
                <Download className="h-3 w-3" />
                Download
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" onClick={() => {
              setBackupCodesOpen(false);
              setBackupCodes(null);
              setCopiedBackupCodes(false);
            }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete your account? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setDeleteAccountOpen(false)}>Cancel</Button>
            <Button size="sm" variant="destructive" onClick={() => {
              handleDeleteAccount().catch(() => {});
            }}>
              Delete account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecuritySettingsPage;
